import { useDataPart } from '@ai-sdk-tools/store';
import { useMutation, useQuery } from '@apollo/client';
import { useFeatureFlag } from '@newfront-insurance/admin-ui';
import { useDownloadFile } from '@newfront-insurance/admin-ui/files';
import type { CoverageGapAnalysisData, PolicyCheckData, SkillOfferData } from '@newfront-insurance/ai-api';
import { useAnalytics } from '@newfront-insurance/data-layer-client';
import { useNotify } from '@newfront-insurance/next-notifications';
import type { UIDataTypes, UIMessagePart, UITools } from 'ai';
import { useState } from 'react';
import type { ReactNode } from 'react';

import { getFileExtension, NOVA_ANALYTICS_EVENTS, trackNovaEvent } from './analytics';
import { stripLeadingSlash } from '../../../lib/skill-command-utils';
import { GET_CUSTOM_SKILLS } from '../../../queries/chats';
import { CREATE_CUSTOM_SKILL } from '../../../queries/mutations';
import { CoverageGapAnalysisStatusCard } from '../../customized-data/coverage-gap-analysis-status-card';
import { PolicyCheckingStatusCard } from '../../customized-data/policy-checking-status-card';
import { ProgressMessage } from '../../ui/progress-message';
import { SkillOfferCard } from '../components/skill-offer-card';
import { createDefaultToolRenderers as createDefaultToolRenderersFromComponents } from '../components/tools/renderers';
import { useChatOverlayContextSafe } from '../contexts/chat-overlay-context';
import { useSkillsSafe } from '../contexts/skills-context';
import { useShowProgressDataPart } from '../hooks/use-show-progress-data-part';

// Type for tool output renderer function
export type ToolOutputRenderer = (part: UIMessagePart<UIDataTypes, UITools>, messageUuid: string) => ReactNode | null;

// Type for data part renderer - now a React component instead of a function
export type DataPartRenderer = () => ReactNode | null;

type SkillOfferDataWithCommandHandle = SkillOfferData & {
  command?: string | null;
  commandHandle?: string | null;
};

/**
 * Coverage Gap Analysis data part component.
 * Uses useDataPart hook to get the latest data.
 */
function CoverageGapAnalysisDataPart() {
  const [data] = useDataPart<CoverageGapAnalysisData>('coverageGapAnalysis');

  if (!data) return null;

  return <CoverageGapAnalysisStatusCard data={data} />;
}

/**
 * Policy Check data part component.
 * Uses useDataPart hook to get the latest data.
 */
function PolicyCheckDataPart() {
  const [data] = useDataPart<PolicyCheckData>('policyCheck');

  if (!data) return null;

  return <PolicyCheckingStatusCard data={data} />;
}

function SkillOfferDataPart() {
  const isNovaSkillsManagementEnabled = useFeatureFlag('nova-skills-management', false);
  const notify = useNotify();
  const analytics = useAnalytics();
  const chatOverlayContext = useChatOverlayContextSafe();
  const [data] = useDataPart<SkillOfferDataWithCommandHandle>('skillOffer');
  const { download, isDownloading } = useDownloadFile({});
  const skillsContext = useSkillsSafe();
  const [isSaving, setIsSaving] = useState(false);
  const [createCustomSkill] = useMutation(CREATE_CUSTOM_SKILL);
  const { data: customSkillsData, refetch: refetchCustomSkills } = useQuery(GET_CUSTOM_SKILLS, {
    variables: {
      filters: {
        enabled: [true, false],
      },
    },
    skip: !isNovaSkillsManagementEnabled || !data?.fileUuid,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });

  if (data?.kind !== 'skill') {
    return null;
  }

  const commandHandle = stripLeadingSlash(data.commandHandle ?? data.command).trim() || undefined;
  const isSaved = Boolean(
    data.fileUuid && customSkillsData?.customSkills?.some((skill) => skill.sourceFileUuid === data.fileUuid),
  );

  const handleDownload = () => {
    if (data.fileUuid) {
      trackNovaEvent(analytics, NOVA_ANALYTICS_EVENTS.GENERATED_SKILL_DOWNLOADED, {
        chatUuid: chatOverlayContext?.chatUuid,
        commandHandle,
        fileExtension: getFileExtension(data.filename),
        fileUuid: data.fileUuid,
      });
      download(data.fileUuid);
    }
  };

  const handleSave = async () => {
    if (!data.fileUuid || isSaving || isSaved) {
      return;
    }

    setIsSaving(true);
    trackNovaEvent(analytics, NOVA_ANALYTICS_EVENTS.GENERATED_SKILL_SAVE_STARTED, {
      chatUuid: chatOverlayContext?.chatUuid,
      commandHandle,
      fileExtension: getFileExtension(data.filename),
      fileUuid: data.fileUuid,
    });
    try {
      const { data: createData } = await createCustomSkill({
        variables: {
          command: {
            fileUuid: data.fileUuid,
            enabled: true,
          },
        },
      });
      await Promise.all([skillsContext?.refetchSkills(), refetchCustomSkills()]);
      trackNovaEvent(analytics, NOVA_ANALYTICS_EVENTS.GENERATED_SKILL_SAVE_SUCCEEDED, {
        chatUuid: chatOverlayContext?.chatUuid,
        commandHandle: createData?.createCustomSkill?.command,
        fileExtension: getFileExtension(data.filename),
        fileUuid: data.fileUuid,
        skillId: createData?.createCustomSkill?.uuid,
        skillSource: createData?.createCustomSkill?.source,
      });
      notify.success('Custom skill saved.');
    } catch (error) {
      trackNovaEvent(analytics, NOVA_ANALYTICS_EVENTS.GENERATED_SKILL_SAVE_FAILED, {
        chatUuid: chatOverlayContext?.chatUuid,
        commandHandle,
        errorName: error instanceof Error ? error.name : undefined,
        fileExtension: getFileExtension(data.filename),
        fileUuid: data.fileUuid,
      });
      notify.error(error instanceof Error ? error.message : 'Unable to save custom skill.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <SkillOfferCard
      commandHandle={commandHandle}
      filename={data.filename}
      onDownload={data.fileUuid ? handleDownload : undefined}
      onSave={isNovaSkillsManagementEnabled ? () => void handleSave() : undefined}
      isDownloading={isDownloading}
      isSaving={isSaving}
      isSaved={isSaved}
      showSave={isNovaSkillsManagementEnabled}
    />
  );
}

export function ProgressDataPart() {
  const showProgressDataPart = useShowProgressDataPart();
  const [data] = useDataPart<{ message?: string }>('progress');

  if (!showProgressDataPart) {
    return null;
  }

  return <ProgressMessage message={data?.message ?? ''} />;
}

/**
 * Creates default data part renderers.
 * Apps can extend or override these by passing their own renderers to Messages component.
 * Each renderer is a React component that uses useDataPart hook internally.
 */
export function createDefaultDataPartRenderers(): Record<string, DataPartRenderer> {
  return {
    coverageGapAnalysis: CoverageGapAnalysisDataPart,
    policyCheck: PolicyCheckDataPart,
    skillOffer: SkillOfferDataPart,
  };
}

/**
 * Creates default tool output renderers.
 * Apps can extend or override these by passing their own renderers to Messages component.
 */
export function createDefaultToolRenderers(): Record<string, ToolOutputRenderer> {
  return createDefaultToolRenderersFromComponents();
}
